<script setup lang="ts">
    import type { MappedCategory } from '~/composables/useNewsCategories';

    interface Props {
        categories?: MappedCategory[] | CategoriesStoryblok;
        link?: StoryblokMultilink;
        param?: string;
        categoryType?: string;
    }

    const { categories = [], categoryType = 'general', param = '', link = undefined } = defineProps<Props>();

    const { country, lang } = useLanguage();
    const countryLanguages = useCountryLanguages();
    const router = useRouter();
    const matchedConfig = useCurrentSectionConfig();

    const newsDataDir = computed(() => {
        const clean = router.currentRoute.value.path.replace(/\/+$/, '');
        const segs = clean.split('/').filter(Boolean);
        const root = segs.at(-1) ?? '';
        const sectionPath = matchedConfig.value
            ? resolveSectionPath(matchedConfig.value, lang.value, countryLanguages)
            : null;

        return sectionPath === null ? null : [sectionPath, root].filter(Boolean).join('/');
    });

    const mappedCategories = ref<MappedCategory[] | CategoriesStoryblok>(categories);

    if (categoryType === 'news') {
        const newsCategories = await useNewsCategories();
        mappedCategories.value = newsCategories.value;
    }

    const newsOverviewBaseUrl = computed(() =>
        newsDataDir.value === null ? '' : buildLocalizedPath(country.value, lang.value, newsDataDir.value)
    );
</script>

<template>
    <div class="sidebar-categories">
        <ul class="first-layer">
            <li
                v-for="category in mappedCategories"
                :key="category.id"
            >
                <NuxtLink
                    :to="
                        categoryType === 'news'
                            ? `${newsOverviewBaseUrl}?category=${category.key}`
                            : useStoryblokUrl(link) + param + category.name
                    "
                >
                    {{ category.nameLocalized ? category.nameLocalized[lang] : category.name || category.value }}
                </NuxtLink>

                <ul
                    v-if="category.subcategories.length"
                    class="second-layer"
                >
                    <li
                        v-for="subcategory in category.subcategories"
                        :key="subcategory.id"
                    >
                        <NuxtLink
                            :to="
                                categoryType === 'news'
                                    ? `${newsOverviewBaseUrl}?category=${subcategory.key}`
                                    : useStoryblokUrl(link) + param + subcategory.name
                            "
                        >
                            {{
                                subcategory.nameLocalized
                                    ? subcategory.nameLocalized[lang]
                                    : subcategory.name || subcategory.value
                            }}
                        </NuxtLink>

                        <ul
                            v-if="subcategory.subcategories.length > 0"
                            class="third-layer"
                        >
                            <li
                                v-for="subsubcategory in subcategory.subcategories"
                                :key="subsubcategory.id"
                            >
                                <NuxtLink
                                    :to="
                                        categoryType === 'news'
                                            ? `${newsOverviewBaseUrl}?category=${subsubcategory.key}`
                                            : useStoryblokUrl(link) + param + subcategory.name
                                    "
                                >
                                    {{
                                        subsubcategory.nameLocalized
                                            ? subsubcategory.nameLocalized[lang]
                                            : subsubcategory.name || subsubcategory.value
                                    }}
                                </NuxtLink>
                            </li>
                        </ul>
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/pages/utils/sidebar-categories.scss';
</style>
